import { Helmet } from 'react-helmet-async';

interface SEOHelmetProps {
  title: string;
  description: string;
  canonical?: string;
  image?: string;
  imageAlt?: string;
  twitterImage?: string;
  type?: 'website' | 'article' | 'product';
  keywords?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
}

/**
 * SEOHelmet - Reusable component for managing meta tags
 *
 * Usage:
 * <SEOHelmet
 *   title="TradeMagic - Trade MTG Cards"
 *   description="The easiest way to trade Magic cards"
 *   canonical="https://trademagic.gg/"
 *   image="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/logos/banner_social_share.png"
 *   twitterImage="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/logos/banner_social_share_twitter.png"
 *   type="website"
 * />
 */
export const SEOHelmet: React.FC<SEOHelmetProps> = ({
  title,
  description,
  canonical,
  image = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/logos/banner_social_share.png',
  imageAlt = title,
  twitterImage = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/logos/banner_social_share_twitter.png',
  type = 'website',
  keywords,
  author = 'TradeMagic',
  publishedTime,
  modifiedTime,
  twitterCard = 'summary_large_image',
}) => {
  // Ensure canonical URL follows standards (lowercase, no trailing slash)
  const canonicalUrl = canonical
    ? canonical.toLowerCase().replace(/\/$/, '')
    : undefined;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {author && <meta name="author" content={author} />}

      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph / Facebook / Discord / LinkedIn */}
      <meta property="og:type" content={type} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:site_name" content="TradeMagic" />

      {/* Article-specific Open Graph tags */}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === 'article' && author && (
        <meta property="article:author" content={author} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      {canonicalUrl && <meta name="twitter:url" content={canonicalUrl} />}
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={twitterImage} />
      <meta name="twitter:image:alt" content={imageAlt} />
      <meta name="twitter:site" content="@trademagic_gg" />
      <meta name="twitter:creator" content="@trademagic_gg" />
    </Helmet>
  );
};
